/* eslint-disable react/prop-types */
import { Card, Col, Divider, Layout, Row, Statistic, Typography } from "antd";
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

const { Title } = Typography;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const month = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const accum = (arr, key) =>
  Object.values(arr).length
    ? Object.values(arr)
        .map((item) => item.reduce((n, data) => n + data[key], 0))
        .reduce((a, b) => a + b, 0)
    : 0;

const platforms = {
  uxrk08UWP9tKE0ehFf67: { title: "Disney+ Hotstar", color: "#0D5C63" },
  zEAGwI1OWReb9aPFQRsV: { title: "MX Player", color: "#44A1A0" },
  WdkSRjlvGdM24eJpym8D: { title: "Hungama", color: "#247B7B" },
  UQDYz1fccATzzM9LvgAW: { title: "BookMyshow Stream", color: "#53C4BC" },
  KcdjLvvTVE3k6vV3kXNq: { title: "Shorted Stream", color: "#A2D5D6" },
};

const dataArr = [
  { key: "views", title: "Total Streams" },
  { key: "users", title: "Total Streaming Users" },
  { key: "time", title: "Total Stream Duration (min)" },
];

const options = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: false },
  },
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxRotation: 45,
        minRotation: 0,
      },
    },
  },
};

// 🔍 Helper to parse "June 2024" into Date
const parseMonthYear = (monthStr) => {
  const [mon, year] = monthStr.split(" ");
  const monthIndex = month.indexOf(mon);
  return new Date(parseInt(year), monthIndex, 1);
};

// 🔍 Get earliest month from metric data
const getEarliestMonth = (data) => {
  const allDates = Object.values(data)
    .flat()
    .map((entry) => parseMonthYear(entry.month));

  return allDates.length
    ? new Date(Math.min(...allDates.map((d) => d.getTime())))
    : new Date(); // fallback
};

// 🔁 Generate all months from start to end
const generateMonthLabels = (start, end) => {
  const labels = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endDate = new Date(end.getFullYear(), end.getMonth(), 1);

  while (current <= endDate) {
    labels.push(month[current.getMonth()] + " " + current.getFullYear());
    current.setMonth(current.getMonth() + 1);
  }

  return labels;
};

const Metric = ({ distribution, lastUpdated }) => {
  const validArr = distribution.platform.filter(
    (item) => item.metric_v2 && ["live", "offline"].includes(item.status)
  );
  const validRevArr = distribution.platform.filter(
    (item) => item.revenue_v2 && ["live", "offline"].includes(item.status)
  );

  const metric =
    validArr.length && lastUpdated
      ? Object.assign(
          {},
          ...validArr.map((item) => ({ [item.id]: item.metric_v2 }))
        )
      : {};
  const revenue =
    validRevArr.length && lastUpdated
      ? Object.assign(
          {},
          ...validRevArr.map((item) => ({ [item.id]: item.revenue_v2 }))
        )
      : {};

  const currentDate = new Date();
  const earliestMetricDate = getEarliestMonth(metric);
  const labels = generateMonthLabels(earliestMetricDate, currentDate);

  const newArr = {};
  const newRevArr = {};

  Object.keys(metric).forEach((x) => {
    newArr[x] = labels.map((label) =>
      metric[x].some((data) => data.month === label)
        ? metric[x]
            .filter((data) => data.month === label)
            .map((x) => ({
              views: parseInt(x.views),
              time: parseInt(x.time),
              users: parseInt(x.users),
            }))[0]
        : { views: 0, time: 0, users: 0 }
    );
  });

  Object.keys(revenue).forEach((x) => {
    newRevArr[x] = labels.map((label) =>
      revenue[x].some((data) => data.month === label)
        ? revenue[x]
            .filter((data) => data.month === label)
            .map((x) => ({
              revenue: parseInt(x.revenue) * 0.9,
            }))[0]
        : { revenue: 0 }
    );
  });

  const data = {
    labels,
    datasets: dataArr.map((item) =>
      Object.keys(newArr).map((x) => ({
        label: platforms[x].title,
        data: newArr[x].map((data) => data[item.key] || 0),
        borderColor: platforms[x].color,
        backgroundColor: platforms[x].color,
        fill: false,
      }))
    ),
  };

  const revData = {
    labels,
    datasets: Object.keys(newRevArr).map((x) => ({
      label: platforms[x].title,
      data: newRevArr[x].map((data) => data["revenue"] || 0),
      borderColor: platforms[x].color,
      backgroundColor: platforms[x].color,
      fill: false,
    })),
  };

  const sum__total_streams = accum(newArr, "views");
  const sum__total_streaming_users = accum(newArr, "users");
  const sum__stream_duration = accum(newArr, "time");
  const sum__share_revenue = accum(newRevArr, "revenue");

  const screenWidth = window ? window.innerWidth : 0;

  const StatisticsCard = ({ screenWidth, title, slug }) => (
    <Col
      xs={{ span: validRevArr.length > 0 ? 12 : 8 }}
      lg={{ span: validRevArr.length > 0 ? 6 : 8 }}
      style={{
        display: screenWidth > 768 ? "block" : "flex",
        marginBottom: "16px",
      }}
    >
      <Card style={{ width: "100%", height: "100%" }}>
        <Statistic title={title} value={slug} />
      </Card>
    </Col>
  );

  return (
    <>
      <Divider />
      <Row>
        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
          <Title level={4}>Streaming Analytics</Title>
        </Col>
      </Row>
      <Layout style={{ background: "white" }}>
        <Row gutter={16} style={{ padding: "8px" }}>
          {validRevArr.length > 0 && (
            <StatisticsCard
              title="Net Revenue"
              slug={`₹ ${parseFloat(sum__share_revenue).toLocaleString(
                "en-IN"
              )}`}
            />
          )}
          <StatisticsCard
            title="Total Streams"
            slug={sum__total_streams.toLocaleString("en-IN")}
          />
          <StatisticsCard
            title="Total Streaming Users"
            slug={sum__total_streaming_users.toLocaleString("en-IN")}
          />
          <StatisticsCard
            title="Total Stream Duration (min)"
            slug={sum__stream_duration.toLocaleString("en-IN")}
          />
        </Row>
        <br />
        <br />
        {distribution && distribution.platform && (
          <>
            {validRevArr.length > 0 && (
              <>
                <Title level={5}>Revenue (INR)</Title>
                <Line
                  options={options}
                  data={revData}
                  height={screenWidth > 768 ? "30%" : "50%"}
                  width="100%"
                />
              </>
            )}
            {data.datasets.map((item, index) => (
              <React.Fragment key={dataArr[index].title}>
                <Title level={5} style={{ marginTop: 64 }}>
                  {dataArr[index].title}
                </Title>
                <Line
                  options={options}
                  data={{ labels, datasets: item }}
                  height={screenWidth > 768 ? "30%" : "50%"}
                  width="100%"
                />
              </React.Fragment>
            ))}
          </>
        )}
      </Layout>
    </>
  );
};

export default Metric;
